import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  globalConstants,
  storeConstants,
  systemConstants,
  TABLE_COLUMNS,
} from "../../constants";
import {
  AsendingSortingIcon,
  DefaultSortingIcon,
  DesendingSortingIcon,
} from "../../assets/icons/iconsProvider";
import { TableCustomHeader } from "../components";
import TableCustomCellWrapper from "../components/table/TableCustomCellWrapper";
import { useDebouncedCallback } from "use-debounce";
import { excludeNullColumns } from "../utility";

/**
 * Hook to add column resize functionality to a table.
 * It allows users to drag column headers to resize them
 * and saves the resized widths in user preferences.
 */
export const useResizableTableColumns = ({
  columns = [],
  tableId,
  resizableColumns = true,
  user,
  store,
  headerCellSelector = "th",
  isSaveColumnSizingInUserPeferences = true,
  updateUserPreferences = () => {},
}) => {
  // Stores current widths of columns { columnKey: width }
  const [columnWidths, setColumnWidths] = useState({});
  const MAX_COL_PERCENT = storeConstants.REACT_DATA_TABLE_MAX_COLUMN_WIDTH;
  const MIN_COL_PERCENT = storeConstants.REACT_DATA_TABLE_MIN_COLUMN_WIDTH;
  const activeResizeRef = useRef(null);
  const tableRef = useRef(null);
  const savedColumnSizing = tableId
    ? user?.userPreferences?.tables?.[tableId]?.columnSizing
    : null;

  const handleUpdateUserPreferences = useDebouncedCallback(
    (updatedColumnSizing) => {
      if (isSaveColumnSizingInUserPeferences) {
        updateUserPreferences(
          user,
          {},
          { tableId, columnSizing: updatedColumnSizing }
        );
      }
    },
    systemConstants.USER_PREFERENCES_DEBOUNCE
  );

  const getColumnKey = useCallback((column, index) => {
    return String(column?.id ?? index);
  }, []);
  const formatColumns = useMemo(() => {
    let filteredColDefs = excludeNullColumns(columns);
    return filteredColDefs.map((column, index) => ({
      ...column,
      __columnKey: getColumnKey(column, index),
    }));
  }, [columns, getColumnKey]);
  const lastColumnKey = useMemo(() => {
    return (
      formatColumns[formatColumns.length - 1]?.id ?? TABLE_COLUMNS.ACTION_ID
    );
  }, [formatColumns]);

  /** ---------- Resize Logic ---------- **/

  const handleResizeMove = useCallback((event) => {
    const active = activeResizeRef.current;
    if (!active || !tableRef.current) return;

    const { columnKey, startX, startWidthPercent } = active;

    const parentWidth =
      tableRef.current.parentElement.offsetWidth ||
      tableRef.current.offsetWidth;
    const deltaPx = event.clientX - startX;
    const deltaPercent = (deltaPx / parentWidth) * 100;

    setColumnWidths((prev) => {
      const next = { ...prev };
      const isActionCol = lastColumnKey === TABLE_COLUMNS.ACTION_ID;
      const actionColWidth = prev[lastColumnKey] ?? (isActionCol ? 12 : 20);

      const sumOtherCols = Object.entries(prev).reduce((sum, [key, val]) => {
        return key !== columnKey && key !== lastColumnKey ? sum + val : sum;
      }, 0);

      const minActionColPercent = isActionCol ? 12 : MIN_COL_PERCENT;
      const maxAllowedWidth = 100 - sumOtherCols - minActionColPercent;

      let newWidth = startWidthPercent + deltaPercent;
      newWidth = Math.max(
        MIN_COL_PERCENT,
        Math.min(newWidth, maxAllowedWidth, MAX_COL_PERCENT)
      );

      const diff = newWidth - prev[columnKey];

      if (!diff) return prev;

      next[columnKey] = newWidth;
      next[lastColumnKey] = actionColWidth - diff;

      handleUpdateUserPreferences(next);
      return next;
    });
    // eslint-disable-next-line
  }, []);

  /**
   * Stops resizing when mouse is released
   * and removes global mouse listeners.
   */
  const handleResizeStop = useCallback(() => {
    activeResizeRef.current = null;
    window.removeEventListener("mousemove", handleResizeMove);
    window.removeEventListener("mouseup", handleResizeStop);
  }, [handleResizeMove]);

  /**
   * Starts resizing when user presses mouse on resizer handle.
   * Stores initial position and width.
   */
  const handleResizeStart = useCallback(
    (event, columnKey) => {
      event.preventDefault();
      event.stopPropagation();
      const isActionCol =
        columnKey === lastColumnKey &&
        lastColumnKey === TABLE_COLUMNS.ACTION_ID;
      activeResizeRef.current = {
        columnKey,
        startX: event.clientX,
        startWidthPercent: columnWidths[columnKey] || (isActionCol ? 12 : 20),
      };

      // Listen globally for mouse movement and release
      window.addEventListener("mousemove", handleResizeMove);
      window.addEventListener("mouseup", handleResizeStop);
    },
    [columnWidths, handleResizeMove, handleResizeStop]
  );

  /**
   * Returns style object for column width.
   * If column was resized, lock width to that value.
   * Otherwise only apply min/max width constraints.
   */
  const getColumnWidthStyle = useCallback(
    (columnKey) => {
      const percent = columnWidths[columnKey];

      if (!percent) return {};

      const isActionCol = columnKey === TABLE_COLUMNS.ACTION_ID;

      return {
        width: `${percent}%`,
        minWidth: isActionCol ? "125px" : `${percent}%`,
        maxWidth: `${percent}%`,
      };
    },
    [columnWidths]
  );

  /**
   * Wraps header content with a resize handle.
   * The handle listens for mouse down to start resizing.
   */
  const renderResizableHeader = useCallback(
    (headerContent, columnKey) => {
      if (!resizableColumns) return headerContent;

      return (
        <div className="rdt-resizable-header">
          <div className="rdt-resizable-header-content">{headerContent}</div>
          {columnKey !== lastColumnKey && (
            <div
              className="rdt-column-resizer"
              onMouseDown={(event) => handleResizeStart(event, columnKey)}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      );
    },
    // eslint-disable-next-line
    [handleResizeStart, resizableColumns]
  );

  const getReactDataTableFormattedColumns = useCallback(() => {
    let tempColumns = formatColumns.map((column, index) => {
      let updatedColumn = { ...column };
      const columnKey = getColumnKey(column, index);

      // ---------------- Column Header text formatting (sort icons, text truncation and tooltip)
      const ColumnHeaderComponent = () => (
        <TableCustomHeader
          id={column.id}
          label={column.name}
          toolTipText={column.toolTipText}
        />
      );
      let columnName = <ColumnHeaderComponent />;
      // add sortable icons if column is sortable
      if (column.sortable) {
        columnName = (
          <div className="d-flex w-100">
            <ColumnHeaderComponent />
            <div className="ml-2 d-flex justify-content-center align-items-center">
              {column.sortDirection === null && (
                <DefaultSortingIcon className="ms-1 cursor-pointer" />
              )}
              {column.sortDirection === globalConstants.SORT.ASC && (
                <AsendingSortingIcon className="ms-1 cursor-pointer" />
              )}
              {column.sortDirection === globalConstants.SORT.DESC && (
                <DesendingSortingIcon className="ms-1 cursor-pointer" />
              )}
            </div>
          </div>
        );
      }
      updatedColumn = { ...updatedColumn, name: columnName };

      // ---------------- Resizable Columns
      if (column.resizeable || resizableColumns) {
        const resolvedName = renderResizableHeader(
          updatedColumn.name,
          columnKey
        );
        const widthStyle = getColumnWidthStyle(columnKey);
        updatedColumn = {
          ...updatedColumn,
          name: resolvedName,
          ...widthStyle,
          style: {
            ...updatedColumn.style,
            ...widthStyle,
          },
        };
      }
      // ------------------Format Column Cell Content (truncate the text)
      if (column.cellValueKey) {
        updatedColumn = {
          ...updatedColumn,
          cell: (row) => <TableCustomCellWrapper row={row} {...column} />,
        };
      }

      // ------------------ apply special last column properties to make it auto resizeable
      if (columnKey === lastColumnKey && resizableColumns) {
        updatedColumn = {
          ...updatedColumn,
          sortable: false,
        };
      }

      return updatedColumn;
    });

    return tempColumns;
    // eslint-disable-next-line
  }, [formatColumns, getColumnKey, renderResizableHeader, getColumnWidthStyle]);

  // ---------------- Initialize widths
  useEffect(() => {
    if (!tableRef.current || !formatColumns.length) {
      return;
    }
    // if saved column sizing available then apply
    else if (
      savedColumnSizing &&
      typeof savedColumnSizing === "object" &&
      Object.keys(savedColumnSizing).length === formatColumns.length
    ) {
      const actionKey = TABLE_COLUMNS.ACTION_ID;
      const currentActionWidth = savedColumnSizing[actionKey];
      if (currentActionWidth !== undefined && currentActionWidth < 12) {
        const adjustedSizing = { ...savedColumnSizing };
        const diff = 12 - currentActionWidth;
        adjustedSizing[actionKey] = 12;

        const keys = Object.keys(adjustedSizing).filter((k) => k !== actionKey);
        if (keys.length > 0) {
          const adjustment = diff / keys.length;
          keys.forEach((k) => {
            adjustedSizing[k] = Math.max(
              MIN_COL_PERCENT,
              (adjustedSizing[k] || 0) - adjustment
            );
          });
        }
        setColumnWidths(adjustedSizing);
      } else {
        setColumnWidths(savedColumnSizing);
      }
    }
    // as no column sizing is saved, apply initial widths
    else if (formatColumns.length) {
      const initialWidths = {};
      const actionColIndex = formatColumns.findIndex(
        (col) =>
          col.id === TABLE_COLUMNS.ACTION_ID ||
          col.field === TABLE_COLUMNS.ACTION_ID
      );
      if (actionColIndex !== -1 && formatColumns.length > 1) {
        const actionColWidth = 12;
        const remainingWidth = 100 - actionColWidth;
        const widthPerOtherColumn = remainingWidth / (formatColumns.length - 1);
        formatColumns.forEach((col, index) => {
          const key = col.__columnKey;
          if (index === actionColIndex) {
            initialWidths[key] = actionColWidth;
          } else {
            initialWidths[key] = widthPerOtherColumn;
          }
        });
      } else {
        const widthPerColumn = 100 / formatColumns.length || 5;
        formatColumns.forEach((col) => {
          const key = col.__columnKey;
          initialWidths[key] = widthPerColumn;
        });
      }
      setColumnWidths(initialWidths);
    }
  }, [formatColumns, savedColumnSizing]);

  /**
   * Cleanup global mouse listeners on unmount
   * to prevent memory leaks.
   */
  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleResizeMove);
      window.removeEventListener("mouseup", handleResizeStop);
    };
  }, [handleResizeMove, handleResizeStop]);

  const formattedColumns = getReactDataTableFormattedColumns();

  return {
    tableRef,
    formatColumns,
    getColumnKey,
    getColumnWidthStyle,
    renderResizableHeader,
    formattedColumns,
  };
};
